export default function Services() {
  const services = [
    {
      icon: 'ri-recycle-line',
      category: 'WASTE TRANSFORMATION',
      title: 'Raw Material Acquisition',
      description: 'Strategic partnerships with mining companies to acquire raw materials that would otherwise be considered waste. We source ROM (Run of Mine) chrome ore from various mines and dumps located around Northwest Province, creating value where none existed before.',
      features: ['Multiple Mine Sources', '70-120km Radius', 'Tailings Reprocessing', 'Dump Site Acquisition'],
      gradient: 'from-[#8B1538] to-[#6B6B6B]',
      image: 'https://public.readdy.ai/ai/img_res/05ad4810e1f68aff459b867ad986d420.jpg'
    },
    {
      icon: 'ri-settings-3-line',
      category: 'PROCESSING',
      title: 'Chrome Ore Beneficiation',
      description: 'Advanced processing plant in Rustenburg transforms ROM into high-grade chrome concentrate. Our facility processes raw material into final product with capacity to produce an average of 1000MT weekly, with plans to enhance output to larger quantities.',
      features: ['ROM to Concentrate', '1000MT Weekly Output', 'Quality Control', 'Continuous Processing'],
      gradient: 'from-[#6B6B6B] to-[#8B1538]',
      image: 'https://readdy.ai/api/search-image?query=chrome%20ore%20processing%20plant%20with%20crushing%20and%20screening%20equipment%2C%20industrial%20mineral%20beneficiation%20facility%20with%20conveyor%20systems%20processing%20dark%20chrome%20ore%2C%20modern%20processing%20infrastructure%20with%20simple%20industrial%20background&width=800&height=600&seq=tmi-mining-002&orientation=landscape'
    },
    {
      icon: 'ri-truck-line',
      category: 'LOGISTICS',
      title: 'Raw Material Transportation',
      description: 'Strategic partnerships with local transporters ensure efficient delivery of raw material from mine to processing plant. Our logistics network covers multiple mine sites within 70-120km radius, ensuring consistent supply to our Rustenburg facility.',
      features: ['Local Transporter Network', 'Mine to Plant Delivery', 'Consistent Supply Chain', 'Route Optimization'],
      gradient: 'from-[#8B1538] to-[#6B6B6B]',
      image: 'https://readdy.ai/api/search-image?query=mining%20trucks%20transporting%20chrome%20ore%20on%20rural%20roads%20in%20South%20Africa%2C%20heavy%20duty%20trucks%20loaded%20with%20dark%20mineral%20ore%20driving%20through%20Northwest%20Province%20landscape%2C%20industrial%20mining%20transportation%20with%20simple%20background&width=800&height=600&seq=tmi-mining-003&orientation=landscape'
    },
    {
      icon: 'ri-store-line',
      category: 'SUPPLY',
      title: 'Chrome Concentrate Sales',
      description: 'Direct supply of quality chrome concentrate to clients from our processing site. As a JIT (Just-In-Time) supplier, we offer competitive pricing by selling directly from source, providing clients with cost advantages over traditional big mining houses.',
      features: ['Direct from Source', 'Competitive Pricing', 'JIT Supply', 'Quality Guarantee'],
      gradient: 'from-[#6B6B6B] to-[#8B1538]',
      image: 'https://readdy.ai/api/search-image?query=processed%20chrome%20concentrate%20in%20industrial%20bags%20ready%20for%20shipment%2C%20high%20grade%20chrome%20ore%20final%20product%20in%20storage%20facility%2C%20quality%20chrome%20mineral%20concentrate%20with%20simple%20industrial%20background&width=800&height=600&seq=tmi-mining-004&orientation=landscape'
    },
    {
      icon: 'ri-plant-line',
      category: 'EXPANSION',
      title: 'Dump Site Development',
      description: 'Exclusive offtake agreements with local communities for dumps left by old mining operations. Plans to deploy yellow machines on dump sites to prepare raw material before trucking to processing site, ensuring consistency in feed supplies.',
      features: ['Community Partnerships', 'On-site Preparation', 'Exclusive Offtake', 'Supply Security'],
      gradient: 'from-[#8B1538] to-[#6B6B6B]',
      image: 'https://readdy.ai/api/search-image?query=old%20mining%20dump%20site%20with%20excavator%20equipment%20preparing%20chrome%20ore%20material%2C%20yellow%20mining%20machinery%20working%20on%20historical%20mine%20tailings%2C%20dump%20site%20rehabilitation%20with%20simple%20industrial%20background&width=800&height=600&seq=tmi-mining-005&orientation=landscape'
    },
    {
      icon: 'ri-search-line',
      category: 'GROWTH',
      title: 'Mine Acquisition Strategy',
      description: 'Actively searching for our own mine to provide consistent supply of raw materials and ensure future security as an asset. This long-term vision demonstrates our commitment to establishing a secure and reliable supply chain for sustainable growth.',
      features: ['Asset Security', 'Supply Consistency', 'Long-term Vision', 'Strategic Growth'],
      gradient: 'from-[#6B6B6B] to-[#8B1538]',
      image: 'https://readdy.ai/api/search-image?query=chrome%20mining%20exploration%20site%20with%20geological%20survey%20equipment%20and%20mineral%20samples%2C%20mining%20feasibility%20study%20location%20with%20chrome%20ore%20deposits%2C%20exploration%20and%20development%20site%20with%20simple%20industrial%20background&width=800&height=600&seq=tmi-mining-006&orientation=landscape'
    }
  ];

  return (
    <section className="py-24 bg-white">
      <div className="max-w-[1400px] mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-5xl md:text-6xl font-bold mb-6 font-['Montserrat']">
            <span className="text-black">OUR CHROME </span>
            <span className="text-[#8B1538]">OPERATIONS</span>
          </h2>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto font-['Lato']">
            From waste material acquisition to premium chrome concentrate production and sales
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8">
          {services.map((service, index) => (
            <div
              key={index}
              className="bg-white rounded-3xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 cursor-pointer border border-gray-100"
            >
              <div className="relative h-[300px] overflow-hidden">
                <img 
                  src={service.image}
                  alt={service.title}
                  className="w-full h-full object-cover object-top"
                />
                <div className={`absolute inset-0 bg-gradient-to-br ${service.gradient} opacity-80 flex items-center justify-center`}>
                  <i className={`${service.icon} text-white text-7xl`}></i>
                </div>
              </div>
              
              <div className="p-8">
                <div className="mb-4">
                  <span className="text-sm text-gray-500 uppercase tracking-wider font-bold">
                    {service.category}
                  </span>
                </div>
                
                <h3 className="text-3xl font-bold text-[#2C3E50] mb-4 leading-tight">
                  {service.title}
                </h3>
                
                <p className="text-gray-600 mb-6 leading-relaxed">
                  {service.description}
                </p>
                
                <div className="grid grid-cols-2 gap-3">
                  {service.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center gap-2">
                      <i className="ri-check-line text-[#8B1538] text-lg"></i>
                      <span className="text-sm text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
